import { createFileRoute, notFound } from "@tanstack/react-router";
import commissionsStyles from "../styles/commissions.css?url";
import { CommissionRequestPage } from "../components/commissions/commission-request";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { loadCommissionRequest } from "../server/commissions";

/**
 * A client's commission request (/commissions/requests/:id?k=…), opened with the private link from
 * their emails. Only that link opens it; it's never cached, kept by the router or indexed, and the
 * page sends no referrer, so the link doesn't travel with a click away.
 */
export const Route = createFileRoute("/commissions/requests/$requestId")({
  validateSearch: (search: Record<string, unknown>) => ({
    k: typeof search.k === "string" ? search.k : "",
    paid: search.paid === "1" || search.paid === 1 ? ("1" as const) : undefined,
  }),
  loaderDeps: ({ search }) => ({ k: search.k }),
  loader: async ({ params, deps }) => {
    const data = await loadCommissionRequest({ data: { id: params.requestId, k: deps.k } });
    if (!data) throw notFound();
    return data;
  },
  staleTime: 0,
  gcTime: 0,
  head: ({ matches, loaderData, params }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head = withStylesheet(
      creatorPageHead(shell, {
        pathname: `/commissions/requests/${params.requestId}`,
        title: `Your request | ${loaderData.creatorName}`,
        description: loaderData.request.serviceTitle,
        noindex: true,
      }),
      commissionsStyles,
    );
    return { ...head, meta: [...(head.meta ?? []), { name: "referrer", content: "no-referrer" }] };
  },
  component: Request,
});

function Request() {
  const data = Route.useLoaderData();
  const { k, paid } = Route.useSearch();
  return (
    <CommissionRequestPage
      key={data.request.id}
      data={data}
      token={k}
      returnedFromPayment={!!paid}
    />
  );
}
